import { fhirClient } from '../../../util/api';
import { formatCodingToken, parseReference } from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { fhirSystems } from './fhir-systems';
import moment from 'moment';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import {
  DataSourceConfig,
  DataSourceParams,
  DataSourceReturnTypes,
  fetchResourcesForPatients,
  mapResourcesToPatient,
  patientServicePageLimit,
} from '../../workflows/datasources';
import { isReferenceMatch } from '../../workflows/utils';

type OnboardingSnapshotDataSourceParams = DataSourceParams<{
  referenceDate: Date;
  timeRange?: [string, string];
}>;

// Helper function to create time range
const createTimeRange = (
  referenceDate: Date,
  monthsBack: number,
  monthsForward: number = 0,
) =>
  [
    `ge${moment(referenceDate)
      .subtract(monthsBack, 'months')
      .startOf('day')
      .toISOString()}`,
    `le${moment(referenceDate)
      .add(monthsForward, 'months')
      .endOf('day')
      .toISOString()}`,
  ] as const;

// Survey Responses Data Source
const surveyResponsesDataSource: DataSourceConfig<
  WithPlatformDefaults<fhir3.QuestionnaireResponse>,
  OnboardingSnapshotDataSourceParams
> = {
  key: 'surveyResponses',
  fetchData: async ({ currentPatientIds, dataset, referenceDate }) => {
    const timeRange = createTimeRange(referenceDate, 13, 3);

    const response = await fhirClient.request(
      'GET /v1/fhir/dstu3/QuestionnaireResponse',
      {
        _tag: [
          dataset.codingToken,
          formatCodingToken({
            system: fhirSystems.questionnaireResponse.type.system,
            code: fhirSystems.questionnaireResponse.type.values.surveyResponse,
          }),
        ],
        patient: currentPatientIds,
        status: 'in-progress,completed',
        pageSize: patientServicePageLimit,
        // Most recent first
        // @ts-expect-error, todo: update service-clients to support QuestionnaireResponse sorting
        _sort: '-authored',
        authored: timeRange,
      },
    );

    return unwrapBundle(response.data);
  },
  mapToPatient: ({ resources, patientId }) => {
    return resources.filter(response =>
      isReferenceMatch({ reference: response?.subject, patientId }),
    );
  },
};

// Consents Data Source
const consentsDataSource: DataSourceConfig<
  WithPlatformDefaults<fhir3.Consent>,
  OnboardingSnapshotDataSourceParams
> = {
  key: 'consents',
  fetchData: async ({ currentPatientIds, dataset, referenceDate }) => {
    const timeRange = createTimeRange(referenceDate, 13, 3);

    const response = await fhirClient.request('GET /v1/fhir/dstu3/Consent', {
      _tag: dataset.codingToken,
      patient: currentPatientIds,
      status: 'active,proposed,rejected',
      pageSize: 1000,
      // @ts-expect-error, todo: update service-clients to support Consent sorting
      _sort: '-date',
      date: timeRange,
    });

    return unwrapBundle(response.data);
  },
  mapToPatient: ({ resources, patientId }) => {
    return resources.filter(consent =>
      isReferenceMatch({ reference: consent?.patient, patientId }),
    );
  },
};

// Helper function to check if appointment has patient as participant
const hasParticipant = ({
  appointment,
  patientId,
}: {
  appointment: WithPlatformDefaults<fhir3.Appointment>;
  patientId: string;
}) =>
  appointment.participant.some(participant => {
    const ref = parseReference(participant?.actor);
    return ref?.resourceType === 'Patient' && ref?.id === patientId;
  });

export const UPCOMING_APPOINTMENT_STATUSES: Array<fhir3.Appointment['status']> =
  ['booked', 'pending', 'proposed', 'arrived'];

const filterAppointmentsToStatus =
  (status: Array<fhir3.Appointment['status']>) =>
  (appointment: fhir3.Appointment) =>
    status.includes(appointment.status);

// Upcoming Appointments Data Source
const upcomingAppointmentsDataSource: DataSourceConfig<
  WithPlatformDefaults<fhir3.Appointment>,
  OnboardingSnapshotDataSourceParams
> = {
  key: 'appointments',
  fetchData: async ({ currentPatientIds, dataset, referenceDate }) => {
    const response = await fhirClient.request(
      'GET /v1/fhir/dstu3/Appointment',
      {
        _tag: [
          dataset.codingToken,
          formatCodingToken({
            system: fhirSystems.source.system,
            code: fhirSystems.source.values.elationAppointment,
          }),
        ],
        patient: currentPatientIds,
        _sort: 'date',
        date: `ge${moment(referenceDate).startOf('day').toISOString()}`,
        pageSize: 1000,
      },
    );

    return unwrapBundle(response.data);
  },
  mapToPatient: ({ resources, patientId }) => {
    return resources.filter(appointment =>
      hasParticipant({ appointment, patientId }),
    );
  },
  processData: appointments => {
    return appointments.filter(
      filterAppointmentsToStatus(UPCOMING_APPOINTMENT_STATUSES),
    );
  },
};

// Key Appointments Data Source
const keyAppointmentsDataSource: DataSourceConfig<
  WithPlatformDefaults<fhir3.Appointment>,
  OnboardingSnapshotDataSourceParams
> = {
  key: 'keyAppointments',
  fetchData: async ({ currentPatientIds, dataset, referenceDate }) => {
    const response = await fhirClient.request(
      'GET /v1/fhir/dstu3/Appointment',
      {
        _tag: [
          dataset.codingToken,
          [
            formatCodingToken({
              system: fhirSystems.appointment.sequence.system,
              code: fhirSystems.appointment.sequence.values.prerequisite,
            }),
            formatCodingToken({
              system: fhirSystems.appointment.reason.system,
              code: fhirSystems.appointment.reason.values.diagnostics,
            }),
          ].join(','),
        ],
        _sort: 'date',
        // Get last 8 months of key appointments
        date: `ge${moment(referenceDate)
          .subtract(8, 'months')
          .startOf('day')
          .toISOString()}`,
        patient: currentPatientIds,
        pageSize: 1000,
      },
    );

    return unwrapBundle(response.data);
  },
  mapToPatient: ({ resources, patientId }) => {
    return resources.filter(appointment =>
      hasParticipant({ appointment, patientId }),
    );
  },
  processData: appointments => {
    return appointments
      .filter(
        filterAppointmentsToStatus([
          ...UPCOMING_APPOINTMENT_STATUSES,
          'fulfilled',
        ]),
      )
      .sort((a, b) => {
        // Sort key appointments by proximity to current reference date
        // If either appointment is missing a start date, place it at the end
        if (!a.start) {
          return 1;
        }
        if (!b.start) {
          return -1;
        }

        const aStart = moment(a.start);
        const bStart = moment(b.start);
        const refDate = moment(); // Current date as reference

        const aDiff = Math.abs(aStart.diff(refDate, 'days'));
        const bDiff = Math.abs(bStart.diff(refDate, 'days'));

        return aDiff - bDiff;
      });
  },
};

// Registry of all data sources
const dataSources = {
  surveyResponses: surveyResponsesDataSource,
  consents: consentsDataSource,
  appointments: upcomingAppointmentsDataSource,
  keyAppointments: keyAppointmentsDataSource,
} as const;

export const fetchSnapshotDataSourcesForPatients = (
  params: OnboardingSnapshotDataSourceParams,
) => fetchResourcesForPatients(dataSources, params);

export const mapSnapshotDataToPatient = (
  resources: DataSourceReturnTypes<typeof dataSources>,
  patientId: string,
) => mapResourcesToPatient(dataSources, resources, patientId);
